import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Vigorbuy Spreadsheet Category Deep Dive: Complete Guide to All 14 Product Categories in 2026',
  description: 'In-depth exploration of every Vigorbuy Spreadsheet product category — Shoes, Hoodies, T-Shirts, Jackets, Accessories, Bags, Perfumes, and more. Find the best products in each category with our detailed guide.',
  author: { '@type': 'Organization', name: 'Vigorbuy Spreadsheet Hub' },
  datePublished: '2026-07-18',
  dateModified: '2026-07-22',
  publisher: { '@type': 'Organization', name: 'Vigorbuy Spreadsheet Hub', url: import.meta.env.VITE_SITE_URL },
  mainEntityOfPage: { '@type': 'WebPage', '@id': import.meta.env.VITE_SITE_URL + '/blog/vigorbuy-spreadsheet-category-deep-dive' },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: import.meta.env.VITE_SITE_URL },
    { '@type': 'ListItem', position: 2, name: 'Vigorbuy Spreadsheet Category Deep Dive', item: import.meta.env.VITE_SITE_URL + '/blog/vigorbuy-spreadsheet-category-deep-dive' },
  ],
};

export default function CategoryDeepDivePage() {
  return (
    <>
      <Navbar />
      <main className="pt-24 pb-16">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
        <article className="max-w-3xl mx-auto px-4 md:px-6">
          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
              <li><Link to="/" className="hover:text-primary-500 transition-colors">Home</Link></li>
              <li><span className="text-foreground-300">/</span></li>
              <li><span className="text-foreground-700 font-medium">Category Deep Dive</span></li>
            </ol>
          </nav>
          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">Categories</span>
              <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">Shopping Guide</span>
              <time className="text-xs text-foreground-400">Updated July 2026</time>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
              Vigorbuy Spreadsheet Category Deep Dive: Complete Guide to All 14 Product Categories
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              One of the biggest advantages of the Vigorbuy Spreadsheet is its comprehensive category system. With 14
              distinct product categories, each meticulously organized and regularly updated, finding exactly what you
              want has never been easier. This deep dive explores every category in detail — what to look for, popular
              finds, and expert tips for each section.
            </p>
          </header>

          <div className="prose-custom space-y-8">
            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                Why Category Organization Matters for Your Shopping Experience
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-3">
                Before diving into individual categories, it is worth understanding why the Vigorbuy Spreadsheet invests
                so heavily in category organization. When you are browsing through hundreds or thousands of products,
                a well-structured category system does more than just keep things tidy — it fundamentally changes how
                efficiently you can shop. Instead of scrolling through an undifferentiated list of items, you can
                immediately narrow your focus to the exact product type you are interested in.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-3">
                The category structure also enables meaningful product comparison. When similar items are grouped
                together, you can evaluate multiple options side by side — comparing prices, materials, sizing notes,
                and QC photos without jumping between different sections or platforms. This consolidated view is
                one of the key reasons experienced shoppers prefer the spreadsheet format over traditional browsing.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                Each category in the Vigorbuy Spreadsheet is actively maintained by community members who have
                expertise in that specific product type. This means you are not just getting a random collection
                of links — you are getting curated selections informed by real-world purchasing experience and
                detailed quality assessment.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                Complete Category Breakdown
              </h2>
              <div className="space-y-6">
                {categories.slice(0, 7).map((cat) => (
                  <div key={cat.slug} className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                        <i className={cat.icon}></i>
                      </span>
                      <h3 className="font-heading text-base font-semibold text-foreground-900">{cat.name}</h3>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{cat.description}</p>
                    <a href={cat.url} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-1 mt-2 text-xs text-primary-600 hover:text-primary-700 whitespace-nowrap">
                      Explore {cat.name} <i className="ri-arrow-right-line text-xs"></i>
                    </a>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                More Categories to Explore
              </h2>
              <div className="space-y-6">
                {categories.slice(7).map((cat) => (
                  <div key={cat.slug} className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                        <i className={cat.icon}></i>
                      </span>
                      <h3 className="font-heading text-base font-semibold text-foreground-900">{cat.name}</h3>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{cat.description}</p>
                    <a href={cat.url} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-1 mt-2 text-xs text-primary-600 hover:text-primary-700 whitespace-nowrap">
                      Explore {cat.name} <i className="ri-arrow-right-line text-xs"></i>
                    </a>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                Tips for Getting the Most from Each Category
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-3">
                Regardless of which category you are browsing, a few universal strategies will help you find the
                best products faster. First, always check the most recently added items in each category — these
                represent the freshest discoveries and are typically from currently active sellers with available
                inventory. Older listings may still be valuable, but availability and pricing can change over time.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-3">
                Second, pay attention to community engagement signals within each category. Products that have
                generated discussion, received multiple QC photo submissions, or been recommended by several
                community members are generally safer bets than listings with minimal community interaction.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                Finally, do not limit yourself to just one or two familiar categories. One of the joys of the
                Vigorbuy Spreadsheet is discovering products you might never have searched for on your own. Browse
                categories like Accessories, Bags, or Other Stuff periodically — you will often find unexpected
                gems that become your favorite purchases.
              </p>
            </section>

            <div className="p-6 md:p-8 rounded-2xl bg-primary-50 border border-primary-200/70 text-center">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
                Ready to Explore All 14 Categories?
              </h3>
              <p className="text-sm text-foreground-600 mb-5 max-w-lg mx-auto">
                Jump into the Vigorbuy Spreadsheet now and start browsing the complete product database
                across every category — all verified, organized, and ready for discovery.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer">
                  Browse All Categories
                  <i className="ri-arrow-right-line"></i>
                </a>
                <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium rounded-full border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-all duration-200 whitespace-nowrap">
                  Back to Homepage
                </Link>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}